import { useCallback, useReducer, useRef, useState } from 'react'
import type { OperationVariables } from '@apollo/client'

import { useActiveUser } from 'Hooks'

import { resolveSubmitColor } from './resolveSubmitColor'
import type { TagAutocompleteHandle } from './TagAutocomplete.types'
import type { Tag } from './tagging.types'
import type { TaggingEntityAdapter } from './taggingEntityAdapter.types'
import type { TaggingContextType } from './TaggingProvider.types'
import { INITIAL_STATE, taggingReducer } from './taggingReducer'
import { useOrgTags } from './useOrgTags'
import { useTagDispatchWrappers } from './useTagDispatchWrappers'
import { useTagKeyboardHandlers } from './useTagKeyboardHandlers'
import { useTagMutationActions } from './useTagMutationActions'
import { useTagOutsideClickDismiss } from './useTagOutsideClickDismiss'

interface UseEntityTaggingOptions<
  TAddData,
  TAddVariables extends OperationVariables,
  TRemoveData,
  TRemoveVariables extends OperationVariables,
> {
  adapter: TaggingEntityAdapter<
    TAddData,
    TAddVariables,
    TRemoveData,
    TRemoveVariables
  >
  entityId: string
  entityTags: Tag[]
  refetch: () => Promise<unknown>
}

export const useEntityTagging = <
  TAddData,
  TAddVariables extends OperationVariables,
  TRemoveData,
  TRemoveVariables extends OperationVariables,
>(
  options: UseEntityTaggingOptions<
    TAddData,
    TAddVariables,
    TRemoveData,
    TRemoveVariables
  >,
): TaggingContextType => {
  const { adapter, entityId, entityTags, refetch } = options
  const { activeOrganisation } = useActiveUser()
  const orgId = String(activeOrganisation?.id ?? '')

  const [state, dispatch] = useReducer(taggingReducer, INITIAL_STATE)
  const [syncAction, setSyncAction] = useState<'add' | 'remove' | null>(null)

  const { orgTags, refetchTags, resolveOrgTagId } = useOrgTags(orgId)

  const autocompleteRef = useRef<TagAutocompleteHandle | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const handleSubmitRef = useRef<() => void>(() => {})
  const entityTagsRef = useRef<Tag[]>(entityTags)

  entityTagsRef.current = entityTags

  const {
    enterEditMode,
    exitEditMode,
    setActiveEditorRowId,
    setInputText,
    setSelectedColor,
  } = useTagDispatchWrappers(dispatch)

  const getEntityIdsWithTag = useCallback(
    (entityIds: string[], tag: Tag) => {
      return entityIds.filter(id => {
        const isCurrentEntity = id === entityId

        return (
          isCurrentEntity &&
          entityTagsRef.current.some(t => !t.inherited && t.id === tag.id)
        )
      })
    },
    [entityId],
  )

  const getEntityIdsWithTagName = useCallback(
    (entityIds: string[], name: string) => {
      return entityIds.filter(id => {
        const isCurrentEntity = id === entityId

        return (
          isCurrentEntity &&
          entityTagsRef.current.some(
            tag => !tag.inherited && tag.name.trim().toLowerCase() === name,
          )
        )
      })
    },
    [entityId],
  )

  const completeTagMutation = useCallback(
    async (affectedEntityIds: string[], onAfterEntityRefetch?: () => void) => {
      let hookCalled = false
      const callHookOnce = () => {
        if (hookCalled) {
          return
        }

        hookCalled = true
        onAfterEntityRefetch?.()
      }

      if (affectedEntityIds.length === 0) {
        callHookOnce()

        return
      }

      let refetchError: unknown

      try {
        await refetch()
      } catch (error) {
        refetchError = error
      } finally {
        callHookOnce()
      }

      try {
        await refetchTags()
      } finally {
        setSyncAction(null)
      }

      if (refetchError) {
        throw refetchError instanceof Error
          ? refetchError
          : new Error('Failed to refetch tagged entity')
      }
    },
    [refetch, refetchTags],
  )

  const {
    addTag,
    isAddingTag: isAddingTagMutation,
    isRemovingTag: isRemovingTagMutation,
    removeTag,
  } = useTagMutationActions({
    adapter,
    getEntityIdsWithTag,
    getEntityIdsWithTagName,
    onAddCompleted: affectedEntityIds =>
      completeTagMutation(affectedEntityIds, () =>
        dispatch({ type: 'EXIT_EDIT_MODE' }),
      ),
    onMutationError: () => setSyncAction(null),
    onMutationStart: action => setSyncAction(action),
    onNoAffected: () => setSyncAction(null),
    onRemoveCompleted: completeTagMutation,
    orgId,
    resolveOrgTagId,
  })

  const isAddingTag = isAddingTagMutation || syncAction === 'add'
  const isRemovingTag = isRemovingTagMutation || syncAction === 'remove'
  const isMutating = isAddingTag || isRemovingTag

  const canSubmit = state.inputText.trim().length > 0
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isMutating) {
      return
    }

    const trimmed = state.inputText.trim()
    const color = resolveSubmitColor(
      state.inputText,
      state.selectedColor,
      orgTags,
    )

    addTag([entityId], trimmed, color)
  }, [
    addTag,
    canSubmit,
    entityId,
    isMutating,
    orgTags,
    state.inputText,
    state.selectedColor,
  ])

  handleSubmitRef.current = handleSubmit

  useTagKeyboardHandlers({
    autocompleteRef,
    exitEditMode,
    handleSubmitRef,
    inputRef,
    inputText: state.inputText,
    isMutating,
    mode: state.mode,
  })

  useTagOutsideClickDismiss({
    exitEditMode,
    inputText: state.inputText,
    isMutating,
    mode: state.mode,
    scopeSelector: adapter.scopeSelector,
  })

  const isEditorOpen = state.mode === 'edit' && state.activeEditorRowId !== null

  return {
    ...state,
    addTag,
    applyingToCountMessageId: adapter.messages.applyingToCount,
    autocompleteRef,
    canSubmit,
    enterEditMode,
    exitEditMode,
    handleSubmit,
    inputRef,
    isAddingTag,
    isEditorOpen,
    isRemovingTag,
    orgTags,
    removeTag,
    setActiveEditorRowId,
    setInputText,
    setSelectedColor,
  }
}
